/**
 * Shared WhatsApp template picker — the one dropdown used wherever a human picks
 * an approved template to send (Chat Logs composer, Campaign form, …). Single
 * source of truth so every picker looks and behaves identically:
 *   - a search box + All / Marketing / Utility category chips (only categories
 *     actually present are shown),
 *   - newest-first rows showing just the human name,
 *   - an image/video/document icon for sendable templates,
 *   - media-UNFIT templates rendered unselectable (dimmed) with a clickable edit
 *     icon that opens the fix/edit screen in a new tab.
 *
 * Callers own what "select" does; this component only surfaces the choice.
 */

import React, { useMemo, useState } from 'react';
import {
  Popover,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  EditOutlined,
  Image as ImageIcon,
  VideoCameraBack,
  Description,
} from '@mui/icons-material';
import {
  isTemplateMediaUnfit,
  templateMediaKind,
  formatTemplateName,
  UNFIT_TEMPLATE_HINT,
} from '@/utils/whatsapp-template-media.util';

/** Minimal shape the picker needs — satisfied by the full template and the lean
 *  "lite" rows the campaign/workflow forms carry. */
export interface PickableTemplate {
  name: string;
  language: string;
  category: string;
  createdAt?: string;
  mediaHealth?: string | null;
  components?: unknown;
}

type CategoryFilter = '' | 'MARKETING' | 'UTILITY';

function MediaKindIcon({ kind }: { kind: 'image' | 'video' | 'document' }): React.ReactElement {
  const sx = { fontSize: 16, color: '#4f46e5', flexShrink: 0 };
  if (kind === 'image') return <ImageIcon sx={sx} />;
  if (kind === 'video') return <VideoCameraBack sx={sx} />;
  return <Description sx={sx} />;
}

export interface TemplatePickerPopoverProps<T extends PickableTemplate> {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Approved, non-AUTHENTICATION templates (caller pre-filters). */
  templates: T[];
  /** Called when a (sendable) template is chosen; the popover then closes. */
  onSelect: (template: T) => void;
  /** New-tab URL to EDIT an unfit template (the editor also re-uploads media).
   *  Omit to hide the edit icon. */
  editUrlFor?: (template: T) => string;
  width?: number;
}

export function TemplatePickerPopover<T extends PickableTemplate>({
  anchorEl,
  open,
  onClose,
  templates,
  onSelect,
  editUrlFor,
  width = 340,
}: TemplatePickerPopoverProps<T>): React.ReactElement {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('');

  // Only show chips for categories actually present (AUTH never appears — excluded
  // upstream — so no empty/misleading tab).
  const categories = useMemo(
    () => (['MARKETING', 'UTILITY'] as const).filter((c) => templates.some((t) => t.category === c)),
    [templates]
  );

  const q = search.trim().toLowerCase();
  const list = useMemo(
    () =>
      templates
        .filter((t) => (category ? t.category === category : true))
        .filter(
          (t) =>
            !q ||
            formatTemplateName(t.name).toLowerCase().includes(q) ||
            t.name.toLowerCase().includes(q)
        )
        .slice()
        .sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        ),
    [templates, category, q]
  );

  const handleClose = () => {
    setSearch('');
    setCategory('');
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width, borderRadius: 2 } } }}
    >
      <Box sx={{ p: 1.25, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        {categories.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {(
              [['', 'All'], ...categories.map((c) => [c, c.charAt(0) + c.slice(1).toLowerCase()] as const)] as const
            ).map(([value, label]) => {
              const active = category === value;
              return (
                <Chip
                  key={value || 'all'}
                  label={label}
                  size="small"
                  onClick={() => setCategory(value as CategoryFilter)}
                  variant={active ? 'filled' : 'outlined'}
                  color={active ? 'primary' : 'default'}
                  sx={{ height: 24, fontSize: '0.7rem' }}
                />
              );
            })}
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ maxHeight: 300, overflowY: 'auto', py: 0.5 }}>
        {list.length === 0 ? (
          <MenuItem disabled>
            {templates.length === 0 ? 'No approved templates yet' : 'No templates match'}
          </MenuItem>
        ) : (
          list.map((t) => {
            // Unfit = can't be sent. Render unselectable (NOT MUI `disabled`, which
            // would also kill the edit icon) with a clickable edit icon to fix it.
            const unfit = isTemplateMediaUnfit(t as never);
            const kind = templateMediaKind(t as never);
            return (
              <MenuItem
                key={`${t.name}:${t.language}`}
                onClick={() => {
                  if (!unfit) {
                    onSelect(t);
                    handleClose();
                  }
                }}
                sx={{
                  gap: 0.75,
                  ...(unfit ? { cursor: 'default', '&:hover': { bgcolor: 'transparent' } } : {}),
                }}
              >
                <Box component="span" sx={{ flex: 1, color: unfit ? 'text.disabled' : 'inherit' }}>
                  {formatTemplateName(t.name)}
                </Box>
                {unfit ? (
                  <Tooltip title={UNFIT_TEMPLATE_HINT}>
                    <IconButton
                      size="small"
                      {...(editUrlFor
                        ? { href: editUrlFor(t), target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25, color: '#b54708' }}
                    >
                      <EditOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  kind && <MediaKindIcon kind={kind} />
                )}
              </MenuItem>
            );
          })
        )}
      </Box>
    </Popover>
  );
}

export default TemplatePickerPopover;
